/** Типы лабораторий обеспеченности */
export const PROVISION_LABORATORY_TYPES = {
  BACTERIOLOGICAL: 1,
  VIROLOGICAL: 2,
  OOI: 3,
};

export const PROVISION_LABORATORY_LABELS = {
  [PROVISION_LABORATORY_TYPES.BACTERIOLOGICAL]: "Бактериологическая лаборатория",
  [PROVISION_LABORATORY_TYPES.VIROLOGICAL]: "Вирусологическая лаборатория",
  [PROVISION_LABORATORY_TYPES.OOI]: "Лаборатория ООИ",
};

/**
 * Фиксированный порядок филиалов (строки отчёта).
 * filialId совпадает с каноническим справочником приложения (PDF logos / filialsMapping):
 * Жамбыл = 7, ЗКО = 6 (не наоборот); Жетісу = 43; Ұлытау = 49;
 * СКО = [13, 22]; Туркестан = [14, 21]; Алматы = [17, 19].
 */
export const PROVISION_BRANCHES = [
  { branchKey: "astana", branchName: "г. Астана", filialId: 16 },
  { branchKey: "almaty", branchName: "г. Алматы", filialId: [17, 19] },
  { branchKey: "shymkent", branchName: "г. Шымкент", filialId: 18 },
  { branchKey: "akmola", branchName: "Акмолинская область", filialId: 2 },
  { branchKey: "aktobe", branchName: "Актюбинская область", filialId: 3 },
  { branchKey: "almaty_obl", branchName: "Алматинская область", filialId: 4 },
  { branchKey: "zhetisu", branchName: "область Жетісу", filialId: 43 },
  { branchKey: "atyrau", branchName: "Атырауская область", filialId: 5 },
  { branchKey: "vko", branchName: "Восточно-Казахстанская область", filialId: 15 },
  { branchKey: "abay", branchName: "область Абай", filialId: 24 },
  { branchKey: "zhambyl", branchName: "Жамбылская область", filialId: 7 },
  { branchKey: "zko", branchName: "Западно-Казахстанская область", filialId: 6 },
  { branchKey: "karaganda", branchName: "Карагандинская область", filialId: 8 },
  { branchKey: "ulytau", branchName: "область Ұлытау", filialId: 49 },
  { branchKey: "kostanay", branchName: "Костанайская область", filialId: 9 },
  { branchKey: "kyzylorda", branchName: "Кызылординская область", filialId: 10 },
  { branchKey: "mangystau", branchName: "Мангистауская область", filialId: 11 },
  { branchKey: "pavlodar", branchName: "Павлодарская область", filialId: 12 },
  { branchKey: "sko", branchName: "Северо-Казахстанская область", filialId: [13, 22] },
  { branchKey: "turkestan", branchName: "Туркестанская область", filialId: [14, 21] },
];

function normalizeBranchName(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[.\s-]+/g, "")
    .trim();
}

export function getCanonicalBranchFilialId(branchKey, branchName) {
  if (branchKey) {
    const byKey = PROVISION_BRANCHES.find((b) => b.branchKey === branchKey);
    if (byKey) return byKey.filialId;
  }
  if (branchName) {
    const normalized = normalizeBranchName(branchName);
    const byName = PROVISION_BRANCHES.find(
      (b) =>
        b.branchName === branchName ||
        normalizeBranchName(b.branchName) === normalized
    );
    if (byName) return byName.filialId;
  }
  return undefined;
}

export function filialIdMatches(spec, employeeFilialId) {
  if (spec == null || employeeFilialId == null) return false;
  const emp = Number(employeeFilialId);
  if (Number.isNaN(emp)) return false;
  if (Array.isArray(spec)) {
    return spec.map(Number).includes(emp);
  }
  return Number(spec) === emp;
}

export function findProvisionBranchByFilialId(employeeFilialId) {
  if (employeeFilialId == null) return null;
  return (
    PROVISION_BRANCHES.find((b) => filialIdMatches(b.filialId, employeeFilialId)) ||
    null
  );
}

const TOTAL_KEY = "total";

export const PROVISION_TOTAL_ROW = {
  branchKey: TOTAL_KEY,
  branchName: "ИТОГО",
  isTotal: true,
};

const pcrGroup = (prefix, title) => ({
  key: `${prefix}_group`,
  title,
  children: [
    {
      key: `${prefix}.balance`,
      title: "Остаток",
      type: "decimal",
      aggregate: "sum",
    },
    {
      key: `${prefix}.expiry`,
      title: "срок годности",
      type: "text",
    },
    {
      key: `${prefix}.planned`,
      title: "Запланировано",
      type: "number",
      aggregate: "sum",
    },
  ],
});

const fundingBlock = (fundingKey, fundingTitle) => ({
  key: fundingKey,
  title: fundingTitle,
  children: [
    pcrGroup(`${fundingKey}.pcr_csf`, "ПЦР (ликвор)"),
    pcrGroup(`${fundingKey}.pcr_swab`, "ПЦР (мазки)"),
    {
      key: `${fundingKey}.note`,
      title: "Примечание",
      type: "text",
    },
  ],
});

/** Типовой набор колонок: Остаток / срок годности / Запланировано / Примечание */
const simpleProvisionColumns = (prefix = "main") => [
  {
    key: `${prefix}.balance`,
    title: "Остаток",
    type: "decimal",
    aggregate: "sum",
  },
  {
    key: `${prefix}.expiry`,
    title: "срок годности",
    type: "text",
  },
  {
    key: `${prefix}.planned`,
    title: "Запланировано",
    type: "number",
    aggregate: "sum",
  },
  {
    key: `${prefix}.note`,
    title: "Примечание",
    type: "text",
  },
];

/** Набор для гриппа/ОРВИ (одинаковый для Бюджет и Внебюджет) */
const fluKitGroup = (key, title) => ({
  key,
  title,
  children: [
    {
      key: `${key}.balance`,
      title: "Остаток (в наборах)",
      type: "decimal",
      aggregate: "sum",
    },
    {
      key: `${key}.studies`,
      title: "Кол-во исследований",
      type: "number",
      aggregate: "sum",
    },
    {
      key: `${key}.expiry`,
      title: "Срок годности",
      type: "text",
    },
    {
      key: `${key}.planned`,
      title: "Запланировано (в наборах)",
      type: "number",
      aggregate: "sum",
    },
  ],
});

const fluOrviColumns = [
  fluKitGroup("flu_ab", "Грипп А/В набор"),
  fluKitGroup("flu_h1n1_h3n2", "Грипп H1N1 и H3N2"),
  fluKitGroup("flu_h1_swine", "Грипп A/H1-swine"),
  fluKitGroup("orvi_screen", "Набор на ОРВИ скрин"),
  {
    key: "note",
    title: "Примечание (статус закупа, поставки и т.п.)",
    type: "text",
  },
];

/**
 * КВИ / Корь / Энтеровирус: Итого + бюджет/платные + примечание
 */
const budgetPaidInfectionColumns = ({
  infectionKey,
  infectionTitle,
  testLabel,
}) => [
  {
    key: "total_available",
    title: "Итого в наличии (в иссл.)",
    type: "computed",
    computeFrom: [
      `${infectionKey}.budget.balance`,
      `${infectionKey}.paid.balance`,
    ],
    aggregate: "sum",
  },
  {
    key: infectionKey,
    title: infectionTitle,
    children: [
      {
        key: `${infectionKey}.budget`,
        title: `${testLabel} (бюджет)`,
        children: [
          {
            key: `${infectionKey}.budget.balance`,
            title: "Остаток (в исследованиях)",
            type: "decimal",
            aggregate: "sum",
          },
          {
            key: `${infectionKey}.budget.expiry`,
            title: "срок годности",
            type: "text",
          },
          {
            key: `${infectionKey}.budget.planned`,
            title: "Запланировано (в исследованиях)",
            type: "number",
            aggregate: "sum",
          },
        ],
      },
      {
        key: `${infectionKey}.paid`,
        title: `${testLabel} (платные)`,
        children: [
          {
            key: `${infectionKey}.paid.balance`,
            title: "Остаток (в исследованиях)",
            type: "decimal",
            aggregate: "sum",
          },
          {
            key: `${infectionKey}.paid.expiry`,
            title: "срок годности",
            type: "text",
          },
          {
            key: `${infectionKey}.paid.planned`,
            title: "Запланировано (в исследованиях)",
            type: "number",
            aggregate: "sum",
          },
        ],
      },
    ],
  },
  {
    key: "note",
    title: "Примечание (статус закупа, поставки и т.п.)",
    type: "text",
  },
];

/**
 * Холера / Сибирская язва (как в Excel):
 * Итого | [инфекция: ПЦР бюджет (3) | Примечание | ПЦР платные (3) | Примечание]
 */
const ooiPcrBudgetPaidWithNotes = ({ infectionKey, infectionTitle }) => [
  {
    key: "total_available",
    title: "Итого в наличии",
    type: "computed",
    computeFrom: [
      `${infectionKey}.budget.balance`,
      `${infectionKey}.paid.balance`,
    ],
    aggregate: "sum",
  },
  {
    key: infectionKey,
    title: infectionTitle,
    children: [
      {
        key: `${infectionKey}.budget`,
        title: `ПЦР - ${infectionTitle} (бюджет)`,
        children: [
          {
            key: `${infectionKey}.budget.balance`,
            title: "Остаток",
            type: "decimal",
            aggregate: "sum",
          },
          {
            key: `${infectionKey}.budget.expiry`,
            title: "срок годности",
            type: "text",
          },
          {
            key: `${infectionKey}.budget.planned`,
            title: "Запланировано",
            type: "number",
            aggregate: "sum",
          },
        ],
      },
      {
        key: `${infectionKey}.budget.note`,
        title: "Примечание (статус закупа, поставки и т.п.)",
        type: "text",
      },
      {
        key: `${infectionKey}.paid`,
        title: `ПЦР - ${infectionTitle} (платные)`,
        children: [
          {
            key: `${infectionKey}.paid.balance`,
            title: "Остаток",
            type: "decimal",
            aggregate: "sum",
          },
          {
            key: `${infectionKey}.paid.expiry`,
            title: "срок годности",
            type: "text",
          },
          {
            key: `${infectionKey}.paid.planned`,
            title: "Запланировано",
            type: "number",
            aggregate: "sum",
          },
        ],
      },
      {
        key: `${infectionKey}.paid.note`,
        title: "Примечание (статус закупа, поставки и т.п.)",
        type: "text",
      },
    ],
  },
];

const metricTriplet = (
  prefix,
  balanceTitle = "Остаток",
  expiryTitle = "срок годности"
) => [
  {
    key: `${prefix}.balance`,
    title: balanceTitle,
    type: "decimal",
    aggregate: "sum",
  },
  {
    key: `${prefix}.expiry`,
    title: expiryTitle,
    type: "text",
  },
  {
    key: `${prefix}.planned`,
    title: "Запланировано",
    type: "number",
    aggregate: "sum",
  },
];

/** Бруцеллез (бюджет/внебюджет одинаковые) */
const brucellosisColumns = [
  {
    key: "infection",
    title: "Бруцеллез",
    children: [
      {
        key: "pcr",
        title: "ПЦР тест-система",
        children: metricTriplet("pcr"),
      },
      {
        key: "ifa",
        title: "ИФА тест-система",
        children: [
          ...metricTriplet("ifa_igm", "Ig M (остаток)"),
          ...metricTriplet("ifa_igg", "Ig G (остаток)"),
        ],
      },
      {
        key: "diagnosticum",
        title: "Диагностикум",
        children: metricTriplet("diagnosticum_ra", "Диагностикум для РА (остаток)"),
      },
    ],
  },
  {
    key: "note",
    title: "Примечание (статус закупа, поставки и т.п.)",
    type: "text",
  },
];

/** Туляремия (бюджет/внебюджет одинаковые) */
const tularemiaColumns = [
  {
    key: "infection",
    title: "Туляремия",
    children: [
      {
        key: "pcr",
        title: "ПЦР тест-система",
        children: metricTriplet("pcr"),
      },
      {
        key: "diagnosticums",
        title: "Диагностикумы",
        children: [
          ...metricTriplet(
            "diag_antigen",
            "Диагностикум антигенный (остаток)"
          ),
          ...metricTriplet(
            "diag_ig",
            "Диагностикум иммуноглобулиновый (остаток)"
          ),
        ],
      },
    ],
  },
  {
    key: "note",
    title: "Примечание (статус закупа, поставки и т.п.)",
    type: "text",
  },
];

/**
 * ККГЛ / Клещевой энцефалит: ПЦР + ИФА (IgM, IgG, Антиген)
 */
const ooiPcrIfaAntigenColumns = (
  infectionTitle,
  expiryTitle = "срок годности"
) => [
  {
    key: "infection",
    title: infectionTitle,
    children: [
      {
        key: "pcr",
        title: "ПЦР тест-система",
        children: metricTriplet("pcr", "Остаток", expiryTitle),
      },
      {
        key: "ifa",
        title: "ИФА тест-система",
        children: [
          ...metricTriplet("ifa_igm", "Ig M (остаток)", expiryTitle),
          ...metricTriplet("ifa_igg", "Ig G (остаток)", expiryTitle),
          ...metricTriplet("ifa_antigen", "Антиген (остаток)", expiryTitle),
        ],
      },
    ],
  },
  {
    key: "note",
    title: "Примечание (статус закупа, поставки и т.п.)",
    type: "text",
  },
];

/** Лайм-боррелиоз / Листериоз: ПЦР + ИФА (IgM, IgG) */
const ooiPcrIfaIgColumns = (infectionTitle) => [
  {
    key: "infection",
    title: infectionTitle,
    children: [
      {
        key: "pcr",
        title: "ПЦР тест-система",
        children: metricTriplet("pcr"),
      },
      {
        key: "ifa",
        title: "ИФА тест-система",
        children: [
          ...metricTriplet("ifa_igm", "Ig M (остаток)"),
          ...metricTriplet("ifa_igg", "Ig G (остаток)"),
        ],
      },
    ],
  },
  {
    key: "note",
    title: "Примечание (статус закупа, поставки и т.п.)",
    type: "text",
  },
];

const tab = (sectionKey, title, titleTemplate, columns, fundingModes = null) => ({
  sectionKey,
  title,
  titleTemplate:
    titleTemplate ||
    `Обеспеченность для диагностики: ${title} на {date}`,
  fundingModes,
  columns,
});

const fundingModesBudgetExtra = [
  { key: "budget", title: "Бюджет" },
  { key: "extrabudget", title: "Внебюджет" },
];

/**
 * Локальная схема (fallback), пока бэкенд не отдал /schema.
 * Грид умеет рисовать любую вложенную schema.columns.
 */
export const PROVISION_FALLBACK_SCHEMAS = {
  [PROVISION_LABORATORY_TYPES.BACTERIOLOGICAL]: {
    laboratoryTypeId: PROVISION_LABORATORY_TYPES.BACTERIOLOGICAL,
    tabs: [
      tab(
        "meningococcal",
        "Менингококковая инфекция",
        "Обеспеченность ПЦР тест-системами для диагностики менингококковой инфекции на {date}",
        [
          fundingBlock("budget", "Бюджет"),
          fundingBlock("paid", "Платные"),
        ]
      ),
    ],
  },
  [PROVISION_LABORATORY_TYPES.VIROLOGICAL]: {
    laboratoryTypeId: PROVISION_LABORATORY_TYPES.VIROLOGICAL,
    tabs: [
      tab(
        "flu_orvi",
        "Грипп и ОРВИ",
        "Обеспеченность наборами ПЦР на грипп и ОРВИ с расчетом на количество исследований ({finance}) на {date}",
        fluOrviColumns,
        fundingModesBudgetExtra
      ),
      tab(
        "coronavirus",
        "Коронавирусная инфекция",
        "Обеспеченность тест-системами для диагностики коронавирусной инфекции (КВИ) на {date}",
        budgetPaidInfectionColumns({
          infectionKey: "cvi",
          infectionTitle: "Коронавирусная инфекция (КВИ)",
          testLabel: "ПЦР - Коронавирусная инфекция",
        })
      ),
      tab(
        "measles",
        "Корь",
        "Обеспеченность ИФА тест-системами для диагностики кори на {date}",
        budgetPaidInfectionColumns({
          infectionKey: "measles_igm",
          infectionTitle: "Корь Ig M",
          testLabel: "ИФА Корь IgM",
        })
      ),
      tab(
        "enterovirus",
        "Энтеровирусная инфекция",
        "Обеспеченность ПЦР тест-системами для диагностики энтеровирусной инфекции на {date}",
        budgetPaidInfectionColumns({
          infectionKey: "enterovirus",
          infectionTitle: "Энтеровирусная инфекция",
          testLabel: "ПЦР - Энтеровирусная инфекция",
        })
      ),
    ],
  },
  [PROVISION_LABORATORY_TYPES.OOI]: {
    laboratoryTypeId: PROVISION_LABORATORY_TYPES.OOI,
    tabs: [
      tab(
        "cholera",
        "Холера",
        "Обеспеченность тест-системами для диагностики холеры на {date}",
        ooiPcrBudgetPaidWithNotes({
          infectionKey: "cholera",
          infectionTitle: "Холера",
        })
      ),
      tab(
        "anthrax",
        "Сибирская язва",
        "Обеспеченность тест-системами для диагностики сибирской язвы на {date}",
        ooiPcrBudgetPaidWithNotes({
          infectionKey: "anthrax",
          infectionTitle: "Сибирская язва",
        })
      ),
      tab(
        "brucellosis",
        "Бруцеллез",
        "Обеспеченность для диагностики бруцеллеза ({finance}) на {date}",
        brucellosisColumns,
        fundingModesBudgetExtra
      ),
      tab(
        "tularemia",
        "Туляремия",
        "Обеспеченность для диагностики туляремии ({finance}) на {date}",
        tularemiaColumns,
        fundingModesBudgetExtra
      ),
      tab(
        "cchf",
        "ККГЛ",
        "Обеспеченность тест-системами для диагностики ККГЛ ({finance}) на {date}",
        ooiPcrIfaAntigenColumns("ККГЛ"),
        fundingModesBudgetExtra
      ),
      tab(
        "tbe",
        "Клещевой энцефалит",
        "Обеспеченность тест-системами для диагностики клещевого энцефалита ({finance}) на {date}",
        ooiPcrIfaAntigenColumns("Клещевой энцефалит", "Срок годности"),
        fundingModesBudgetExtra
      ),
      tab(
        "lyme",
        "Лайм-боррелиоз",
        "Обеспеченность тест-системами для диагностики клещевого боррелиоза ({finance}) на {date}",
        ooiPcrIfaIgColumns("Лайм-Боррелиоз"),
        fundingModesBudgetExtra
      ),
      tab(
        "listeriosis",
        "Листериоз",
        "Обеспеченность тест системами для диагностики листериоза ({finance}) на {date}",
        ooiPcrIfaIgColumns("Листериоз"),
        fundingModesBudgetExtra
      ),
    ],
  },
};

/** Рекурсивно собирает листовые колонки */
export function flattenLeafColumns(columns = []) {
  const leaves = [];
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      if (node.children?.length) {
        walk(node.children);
      } else if (node.key) {
        leaves.push(node);
      }
    });
  };
  walk(columns);
  return leaves;
}

/** Глубина дерева колонок */
export function getColumnTreeDepth(columns = []) {
  if (!columns?.length) return 0;
  return (
    1 +
    Math.max(
      0,
      ...columns.map((c) =>
        c.children?.length ? getColumnTreeDepth(c.children) : 0
      )
    )
  );
}

/** Количество листьев под узлом */
export function countLeafColumns(node) {
  if (!node.children?.length) return 1;
  return node.children.reduce((sum, child) => sum + countLeafColumns(child), 0);
}

/**
 * Строит матрицу ячеек thead (rowspan/colspan) для multi-level header.
 * Возвращает массив строк: [{ title, colSpan, rowSpan, key }]
 */
export function buildHeaderRows(columns = []) {
  const depth = getColumnTreeDepth(columns);
  if (!depth) return [];

  const rows = Array.from({ length: depth }, () => []);

  const walk = (nodes, level) => {
    (nodes || []).forEach((node) => {
      const leafCount = countLeafColumns(node);
      const hasChildren = !!node.children?.length;
      rows[level].push({
        key: node.key || `${node.title}_${level}`,
        title: node.title,
        colSpan: leafCount,
        rowSpan: hasChildren ? 1 : depth - level,
        isLeaf: !hasChildren,
        type: node.type,
      });
      if (hasChildren) {
        walk(node.children, level + 1);
      }
    });
  };

  walk(columns, 0);
  return rows;
}

export function formatReportDateRu(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export function toApiDate(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Число с запятой → number | null */
export function parseDecimalComma(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized || normalized === "-" || normalized === ".") return null;
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
}

/** number → отображение с запятой */
export function formatDecimalComma(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") {
    return String(value).replace(".", ",");
  }
  return String(value).replace(".", ",");
}

export function isDecimalInputValid(raw) {
  if (raw === "" || raw === null || raw === undefined) return true;
  return /^-?\d*(,\d*)?$/.test(String(raw).trim());
}

export function resolveTitle(template, date, extras = {}) {
  const formatted = formatReportDateRu(date);
  if (!template) return formatted;
  let text = String(template)
    .replace("{date}", formatted)
    .replace("{дата}", formatted);
  if (extras.finance) {
    text = text.replace("{finance}", extras.finance).replace("{финанс}", extras.finance);
  } else {
    text = text.replace(/\s*\(\{finance\}\)/g, "").replace("{finance}", "");
  }
  return text;
}

/** Значение вычисляемой колонки по строке */
export function getComputedCellValue(col, cells = {}) {
  if (!col || col.type !== "computed") return null;
  const keys = col.computeFrom || [];
  return keys.reduce((acc, key) => {
    const v = cells?.[key];
    const n = typeof v === "number" ? v : parseDecimalComma(v);
    return acc + (n == null ? 0 : n);
  }, 0);
}

/**
 * Роли модуля «Обеспеченность»
 * SuperAdministrator / Administrator — полный доступ
 * AdministratorReadonly — только просмотр
 * ProvisionSupervisor — заполнение своей лаборатории + своей строки филиала
 */
export const PROVISION_ACCESS_ROLES = [
  "SuperAdministrator",
  "Administrator",
  "AdministratorReadonly",
  "ProvisionSupervisor",
];

/**
 * Определяет laboratoryTypeId (1|2|3) по лаборатории сотрудника.
 * Сопоставление по названию department (id в разных средах могут отличаться).
 */
export function resolveLaboratoryTypeIdFromEmployee(employee) {
  if (!employee) return null;

  const name = String(
    employee.department?.name ||
      employee.departmentName ||
      employee.Department?.name ||
      ""
  )
    .toLowerCase()
    .replace(/ё/g, "е");

  if (!name) return null;

  if (/бактериолог/.test(name)) {
    return PROVISION_LABORATORY_TYPES.BACTERIOLOGICAL;
  }
  if (/вирусолог/.test(name)) {
    return PROVISION_LABORATORY_TYPES.VIROLOGICAL;
  }
  if (
    /оои/.test(name) ||
    /особо\s*опасн/.test(name) ||
    /карантинн/.test(name)
  ) {
    return PROVISION_LABORATORY_TYPES.OOI;
  }

  return null;
}

export function getProvisionAccess(roles = [], employee = null) {
  const list = Array.isArray(roles) ? roles : [];
  const has = (code) => list.includes(code);

  const isFullEditor =
    has("SuperAdministrator") || has("Administrator");
  const isFiller = has("ProvisionSupervisor");
  const isReadonly =
    has("AdministratorReadonly") && !isFullEditor && !isFiller;
  const canView =
    isFullEditor || isFiller || has("AdministratorReadonly");
  const canEdit = isFullEditor || isFiller;

  // null = все лаборатории; [] = нет доступных; [n] = только эти
  let allowedLaboratoryTypeIds = null;
  if (isFiller && !isFullEditor) {
    const labTypeId = resolveLaboratoryTypeIdFromEmployee(employee);
    allowedLaboratoryTypeIds = labTypeId != null ? [labTypeId] : [];
  }

  return {
    canView,
    canEdit,
    isFullEditor,
    isFiller,
    isReadonly: isReadonly || !canEdit,
    allowedLaboratoryTypeIds,
    canAccessLaboratory(laboratoryTypeId) {
      if (allowedLaboratoryTypeIds == null) return true;
      return allowedLaboratoryTypeIds
        .map(Number)
        .includes(Number(laboratoryTypeId));
    },
  };
}

function isSameProvisionBranch(row, meta) {
  if (!row || !meta) return false;
  if (row.branchKey && row.branchKey === meta.branchKey) return true;
  const rowName = normalizeBranchName(row.branchName);
  return !!rowName && rowName === normalizeBranchName(meta.branchName);
}

/**
 * Можно ли редактировать строку филиала.
 * Full editor — все строки; ProvisionSupervisor — только свой филиал.
 * Проверяем filialId сотрудника по filialIds строки (например, филиал
 * может включать несколько id). Для старых данных поддерживаем filialId.
 * filialId из schema/отчёта не должен
 * закрывать свою строку и открывать чужую.
 */
export function canEditBranchRow(row, employee, access) {
  if (!row || row.isTotal || row.branchKey === TOTAL_KEY) return false;

  const rights =
    typeof access === "boolean"
      ? { isFullEditor: access, canEdit: access }
      : access || {};

  if (!rights.canEdit) return false;
  if (rights.isFullEditor) return true;

  const empFilialId = employee?.filialId ?? employee?.FilialId;
  if (empFilialId == null) return false;

  const rowFilial =
    row.filialIds ??
    row.filialId ??
    getCanonicalBranchFilialId(row.branchKey, row.branchName);
  return filialIdMatches(rowFilial, empFilialId);
}
